import * as React from 'react';

import { CoverageCategoryChangedEvent } from '@/components/task-activity/events/CoverageCategoryChangedEvent';
import { CoverageCreatedEvent } from '@/components/task-activity/events/CoverageCreatedEvent';
import { CoverageStatusChangedEvent } from '@/components/task-activity/events/CoverageStatusChangedEvent';
import { CoverageUpdatedEvent } from '@/components/task-activity/events/CoverageUpdatedEvent';
import { FallbackEvent } from '@/components/task-activity/events/FallbackEvent';
import { LOAChaserEvent } from '@/components/task-activity/events/LOAChaserEvent';
import { MatterRetainerSignedByAttorneyEvent } from '@/components/task-activity/events/MatterRetainerSignedByAttorneyEvent';
import { MatterRetainerSignedByClientEvent } from '@/components/task-activity/events/MatterRetainerSignedByClientEvent';
import { MatterRetainerSignedEvent } from '@/components/task-activity/events/MatterRetainerSignedEvent';
import { PoliceReportCreatedEvent } from '@/components/task-activity/events/PoliceReportCreatedEvent';
import { PoliceReportStatusChangedEvent } from '@/components/task-activity/events/PoliceReportStatusChangedEvent';
import { PoliceReportUpdatedEvent } from '@/components/task-activity/events/PoliceReportUpdatedEvent';
import { ProviderCreatedEvent } from '@/components/task-activity/events/ProviderCreatedEvent';
import { ProviderCuratedEvent } from '@/components/task-activity/events/ProviderCuratedEvent';
import { ProviderStatusChangedEvent } from '@/components/task-activity/events/ProviderStatusChangedEvent';
import { ProviderTreatmentStatusChangedEvent } from '@/components/task-activity/events/ProviderTreatmentStatusChangedEvent';
import { TaskExternalCmsUpdateStatusChangedEvent } from '@/components/task-activity/events/TaskExternalCmsUpdateStatusChangedEvent';
import { TreatmentRequestCreatedEvent } from '@/components/task-activity/events/TreatmentRequestCreatedEvent';
import { TreatmentRequestResearchCompletedEvent } from '@/components/task-activity/events/TreatmentRequestResearchCompletedEvent';
import { TreatmentRequestStatusChangedEvent } from '@/components/task-activity/events/TreatmentRequestStatusChangedEvent';
import { TreatmentRequestUpdatedEvent } from '@/components/task-activity/events/TreatmentRequestUpdatedEvent';
import type { TaskActivity } from '@/services/api/generated/taskActivityService';

import { isConfiguredTaskEvent } from './timelineConfig';
import { TimelineItemEvent } from './TimelineItemEvent';

interface EventRendererProps {
  activityItem: TaskActivity;
  matterId: string;
  defaultCollapsed?: boolean;
}

/**
 * Renders the appropriate event component based on event_code.
 * Used by both Timeline and RelatedActivityTimeline.
 */
export const TimelineEventRouter: React.FC<EventRendererProps> = ({
  activityItem,
  matterId,
  defaultCollapsed = false,
}) => {
  const { event } = activityItem;

  // Handle LOA Chaser events (event types not yet in generated API types)
  if ((event.event_code as string).startsWith('loa_chaser_')) {
    return <LOAChaserEvent activityItem={activityItem} />;
  }

  // Handle task events (field changes + creation) via config-driven component
  if (isConfiguredTaskEvent(event.event_code)) {
    return <TimelineItemEvent event={event} defaultCollapsed={defaultCollapsed} />;
  }

  // Render the appropriate event component based on event_code
  switch (event.event_code) {
    case 'coverage_created':
      return <CoverageCreatedEvent event={event} matterId={matterId} />;

    case 'coverage_updated':
      return <CoverageUpdatedEvent event={event} matterId={matterId} />;

    case 'coverage_category_changed':
      return <CoverageCategoryChangedEvent event={event} matterId={matterId} />;

    case 'coverage_status_changed':
      return <CoverageStatusChangedEvent event={event} matterId={matterId} />;

    case 'task_external_cms_update_status_changed':
      return (
        <TaskExternalCmsUpdateStatusChangedEvent
          event={event}
          matterId={matterId}
        />
      );

    case 'matter_retainer_signed':
      return <MatterRetainerSignedEvent event={event} matterId={matterId} />;

    case 'matter_retainer_signed_by_client':
      return (
        <MatterRetainerSignedByClientEvent event={event} matterId={matterId} />
      );

    case 'matter_retainer_signed_by_attorney':
      return (
        <MatterRetainerSignedByAttorneyEvent
          event={event}
          matterId={matterId}
        />
      );

    case 'police_report_created':
      return <PoliceReportCreatedEvent event={event} matterId={matterId} />;

    case 'police_report_updated':
      return <PoliceReportUpdatedEvent event={event} matterId={matterId} />;

    case 'police_report_status_changed':
      return (
        <PoliceReportStatusChangedEvent event={event} matterId={matterId} />
      );

    case 'provider_created':
      return <ProviderCreatedEvent event={event} matterId={matterId} />;

    case 'provider_status_changed':
      return <ProviderStatusChangedEvent event={event} matterId={matterId} />;

    case 'provider_treatment_status_changed':
      return (
        <ProviderTreatmentStatusChangedEvent
          event={event}
          matterId={matterId}
        />
      );

    case 'provider_curated':
      return <ProviderCuratedEvent event={event} matterId={matterId} />;

    case 'treatment_request_research_completed':
      return (
        <TreatmentRequestResearchCompletedEvent
          event={event}
          matterId={matterId}
        />
      );

    case 'treatment_request_created':
      return <TreatmentRequestCreatedEvent event={event} matterId={matterId} />;

    case 'treatment_request_updated':
      return <TreatmentRequestUpdatedEvent event={event} matterId={matterId} />;

    case 'treatment_request_status_changed':
      return (
        <TreatmentRequestStatusChangedEvent event={event} matterId={matterId} />
      );

    // For events that don't have a component yet, show a fallback
    default:
      return <FallbackEvent activityItem={activityItem} />;
  }
};
